import { ChevronRight, Close, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HandlersLibraryTreeNode,
  useGetApiLibraryTreeQuery,
  usePostApiLibraryMoveByIdAndNewParentIdMutation,
} from '../../../../app/athenaApiGen';
import {
  setInfoBarDetail,
  setMoveTargetId,
  toggleModal,
} from '../../../../app/library/librarySlice';
import { RootState } from '../../../../app/store';
import { getEntityIcon } from '../entityIcon';

// Recursively find a node by id + return ancestor chain (excluding the node).
const findWithPath = (
  nodes: HandlersLibraryTreeNode[] | undefined,
  targetId: string,
  path: HandlersLibraryTreeNode[] = [],
): { node: HandlersLibraryTreeNode; path: HandlersLibraryTreeNode[] } | null => {
  if (!nodes) return null;
  for (const n of nodes) {
    if (n.id === targetId) return { node: n, path };
    if (n.children?.length) {
      const r = findWithPath(n.children, targetId, [...path, n]);
      if (r) return r;
    }
  }
  return null;
};

// Collect every descendant id under a node (inclusive of node itself).
const collectDescendantIds = (node: HandlersLibraryTreeNode): Set<string> => {
  const out = new Set<string>();
  const walk = (n: HandlersLibraryTreeNode) => {
    if (n.id) out.add(n.id);
    n.children?.forEach(walk);
  };
  walk(node);
  return out;
};

// Only container types are valid destinations.
const isContainerType = (t?: string) =>
  t === 'program' || t === 'project' || t === 'library_directory';

// Render one tree row recursively.
type RowProps = {
  node: HandlersLibraryTreeNode;
  depth: number;
  expandedIds: Set<string>;
  selectedId: string | null;
  invalidIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
};

const PickerRow = ({
  node,
  depth,
  expandedIds,
  selectedId,
  invalidIds,
  onToggle,
  onSelect,
}: RowProps) => {
  if (!node.id) return null;
  const isInvalid = invalidIds.has(node.id);
  const isContainer = isContainerType(node.entityType);
  // Show containers only — files / deliverables aren't valid drop targets.
  // (Deliverables CAN host attachments, but to keep v1 simple we only allow
  // moves into program/project/folder. Tighten or loosen later.)
  if (!isContainer) return null;
  const childContainers = (node.children ?? []).filter((c) =>
    isContainerType(c.entityType),
  );
  const hasChildren = childContainers.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = !isInvalid && selectedId === node.id;

  return (
    <>
      <Box
        onClick={() => !isInvalid && onSelect(node.id!)}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: 1 + depth * 1.5,
          pr: 1,
          py: 0.5,
          cursor: isInvalid ? 'not-allowed' : 'pointer',
          opacity: isInvalid ? 0.4 : 1,
          borderRadius: 0.5,
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : 'transparent',
          '&:hover': {
            backgroundColor: isInvalid
              ? 'transparent'
              : isSelected
                ? theme.palette.action.selected
                : theme.palette.action.hover,
          },
        })}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            sx={{ p: 0, width: 20, height: 20 }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id!);
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 20 }} />
        )}
        {getEntityIcon(node.entityType, isExpanded)}
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', userSelect: 'none' }}
        >
          {node.label}
        </Typography>
      </Box>
      {isExpanded &&
        childContainers.map((child) => (
          <PickerRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            selectedId={selectedId}
            invalidIds={invalidIds}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </>
  );
};

const MovePickerModal = () => {
  const dispatch = useDispatch();
  const { modals, moveTargetId } = useSelector(
    (state: RootState) => state.library,
  );
  const { data: treeData } = useGetApiLibraryTreeQuery({ includeAll: false });
  const [moveItem, { isLoading: moving }] =
    usePostApiLibraryMoveByIdAndNewParentIdMutation();

  // Find the item being moved + its current parent.
  const moveInfo = useMemo(() => {
    if (!moveTargetId || !treeData) return null;
    return findWithPath(treeData, moveTargetId);
  }, [treeData, moveTargetId]);

  const movingNode = moveInfo?.node;
  const currentParent =
    moveInfo?.path[moveInfo.path.length - 1] ?? null;

  // Disabled destinations: the node itself, its descendants (cycle),
  // and its current parent (no-op).
  const invalidIds = useMemo(() => {
    if (!movingNode) return new Set<string>();
    const set = collectDescendantIds(movingNode);
    if (currentParent?.id) set.add(currentParent.id);
    return set;
  }, [movingNode, currentParent]);

  // Default-expand the path to the moving item so the user sees nearby
  // alternatives immediately.
  const initialExpanded = useMemo(() => {
    const set = new Set<string>();
    moveInfo?.path.forEach((n) => n.id && set.add(n.id));
    return set;
  }, [moveInfo]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(initialExpanded);
  const [selectedDestinationId, setSelectedDestinationId] = useState<
    string | null
  >(null);

  // Reset modal state whenever it opens.
  useEffect(() => {
    if (modals.movePicker.open) {
      setExpandedIds(initialExpanded);
      setSelectedDestinationId(null);
    }
  }, [modals.movePicker.open, initialExpanded]);

  const handleToggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleClose = () => {
    dispatch(toggleModal('movePicker'));
    dispatch(setMoveTargetId(null));
  };

  const handleMove = async () => {
    if (!moveTargetId || !selectedDestinationId) return;
    try {
      await moveItem({
        id: moveTargetId,
        newParentId: selectedDestinationId,
      }).unwrap();
      dispatch(setInfoBarDetail('Moved successfully.'));
      // The move mutation invalidates 'Library' itself, so the tree refetches.
      handleClose();
    } catch (err: any) {
      dispatch(
        setInfoBarDetail(
          err?.data?.details ?? 'Move failed. Please try again.',
        ),
      );
    }
  };

  // Build the destination path string for the bottom of the modal.
  const destinationPath = useMemo(() => {
    if (!selectedDestinationId || !treeData) return null;
    const r = findWithPath(treeData, selectedDestinationId);
    if (!r) return null;
    return [...r.path.map((n) => n.label), r.node.label]
      .filter(Boolean)
      .join(' / ');
  }, [selectedDestinationId, treeData]);

  return (
    <Dialog
      open={modals.movePicker.open}
      onClose={handleClose}
      PaperProps={{ sx: { minWidth: 480, maxWidth: 560 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box component="span">
          Move <strong>{movingNode?.label ?? 'item'}</strong> to…
        </Box>
        <IconButton onClick={handleClose} disabled={moving} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            py: 1,
          }}
        >
          {(treeData ?? []).map((root) => (
            <PickerRow
              key={root.id}
              node={root}
              depth={0}
              expandedIds={expandedIds}
              selectedId={selectedDestinationId}
              invalidIds={invalidIds}
              onToggle={handleToggle}
              onSelect={setSelectedDestinationId}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, py: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minHeight: 18 }}
        >
          {destinationPath
            ? `Destination: ${destinationPath}`
            : 'Pick a destination folder above.'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={moving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMove}
            disabled={!selectedDestinationId || moving}
          >
            {moving ? 'Moving…' : 'Move'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MovePickerModal;
